import styled from '@emotion/styled';
import type { Row } from '@innovamat/glimmer-components';
import {
  Drawer,
  IconButton,
  Typography,
  useGlimmerTheme,
} from '@innovamat/glimmer-components';
import { useTranslation } from 'react-i18next';
import type { BlocksAverageReport } from '../../views/general-progress/utils/types';
import { DigitalPracticeBarChart } from './student-drawer.chart';
import { StudentDrawerDigitalPractice } from './student-drawer.digital-practice';
import { StudentDrawerScores } from './student-drawer.scores';
import {
  getAveragesFromClassroomBlockAverages,
  getAveragesFromStudentBlocks,
} from './utils/get-average-data';
import type { StudentDrawerData } from './utils/types';

type Props = {
  classroomBlockAverages: BlocksAverageReport;
  classroomName?: string;
  isNextDisabled?: boolean;
  isOpen: boolean;
  isPreviousDisabled?: boolean;
  student?: Row;
  onClickNext?: () => void;
  onClickPrevious?: () => void;
  onClose?: () => void;
};

export function StudentDrawer({
  classroomBlockAverages,
  classroomName,
  isNextDisabled,
  isOpen,
  isPreviousDisabled,
  student,
  onClickNext,
  onClickPrevious,
  onClose,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const theme = useGlimmerTheme();

  return (
    <Drawer
      closeOnClickOutside
      customStyles={{ padding: '0 32px', maxWidth: '1088px', width: '80vw' }}
      open={isOpen}
      roundedBorder
      onClose={onClose}
    >
      <Header>
        <Text>
          <Typography.SuperTitle
            color={`${theme.tokens.color.alias.cm.text['text-subtle'].value}`}
          >
            {classroomName}
          </Typography.SuperTitle>
          <Typography.H3>
            {student?.firstName} {student?.lastName}
          </Typography.H3>
        </Text>
        <ActionsWrapper>
          <IconButton
            icon={'PreviousIcon'}
            size="M"
            onClick={() => onClickPrevious?.()}
            state={isPreviousDisabled ? 'disabled' : undefined}
          />
          <IconButton
            icon={'NextIcon'}
            size="M"
            onClick={() => onClickNext?.()}
            state={isNextDisabled ? 'disabled' : undefined}
          />
          <ActionsDivider />
          <IconButton
            size="M"
            tooltipText={t('reports.asideModal.close')}
            onClick={onClose}
            icon="CloseIcon"
          />
        </ActionsWrapper>
      </Header>
      <Content>
        <DigitalPracticeWrapper>
          <Section>
            <Typography.H4>
              {t('reports.student-drawer.digital-practice')}
            </Typography.H4>
            <StudentDrawerDigitalPractice row={student} />
          </Section>
          <Section>
            <Typography.H4>
              {t('reports.student-drawer.score')}
              <SubtleSpan>
                {t('reports.student-drawer.score-average')}
              </SubtleSpan>
              <DigitalPracticeBarChart
                classroomAverages={getAveragesFromClassroomBlockAverages(
                  classroomBlockAverages
                )}
                studentAverages={getAveragesFromStudentBlocks(student?.blocks)}
              />
            </Typography.H4>
          </Section>
        </DigitalPracticeWrapper>
        <Section>
          <Typography.H4>
            {t('reports.student-drawer.course-content')}
          </Typography.H4>
          <StudentDrawerScores
            data={student as StudentDrawerData}
            drawerScoreData={student?.blocks}
          />
        </Section>
      </Content>
    </Drawer>
  );
}

const ActionsWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const Text = styled.div`
  display: flex;
  flex-direction: column;
`;

const ActionsDivider = styled.div`
  width: 1px;
  height: 20px;
  background-color: ${({ theme }) =>
    theme.tokens.color.alias.cm.border['border-subtle'].value};
  margin: 0 16px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 40px 0 8px;
  border-bottom: 1px solid
    ${({ theme }) => theme.tokens.color.alias.cm.border['border-subtle'].value};
`;

const Content = styled.div`
  padding: 32px 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FlexContainer = styled.div`
  display: flex;
  gap: 24px;
`;

const DigitalPracticeWrapper = styled(FlexContainer)`
  display: flex;
  gap: 32px;
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
  }
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
`;

const SubtleSpan = styled(Typography.Body2)`
  display: inline;
  color: ${({ theme }) =>
    theme.tokens.color.alias.cm.text['text-subtle'].value};
  margin-left: 4px;
`;
